import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { alternativeAmountError, convertorMessage, isPix, OF_ALTERNATIVE, OF_ALTERNATIVE_ADD, type AlternativeMethod } from './cardsData'
import { useCards } from './context'
import { Alert, DotsMenu, OfLink, OfOnly } from './parts'
import { Loader } from './Transactions'

/** /my/payments/alternative: the creator's alternative top-up methods. */
export function AlternativeTab() {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { alternatives, me } = useCards()
  const list = alternatives ?? []
  const title = isPix(list) ? 'Pix' : t('alt.title')
  return (
    <div className="ofc-alternative">
      <div className={`ofc-filter ${list.length ? 'has-list' : ''}`}>
        <h2 className="ofc-filter-title">{title}</h2>
        <button type="button" className="ofc-icon-btn is-gray" aria-label={t('addAlternative')} title={`${t('addAlternative')} — ${t('only.altAdd')}`} disabled>
          <FeatherIcon name="plus" size={22} />
        </button>
      </div>
      {list.length ? (
        list.map((m) => <AlternativeItem key={m.id} method={m} />)
      ) : alternatives === null ? (
        <Loader />
      ) : (
        <Alert variant="danger">
          {t('alt.none')} {!me.isDeleteInitiated && <OfLink href={OF_ALTERNATIVE_ADD}>{t('alt.add')}</OfLink>}
        </Alert>
      )}
      <OfOnly text={t('only.altAdd')} href={OF_ALTERNATIVE_ADD} />
    </div>
  )
}

/** PaymentsAlternativeItem: name, currency, details, Delete, and the amount to pay. */
function AlternativeItem({ method }: { method: AlternativeMethod }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'cardsPage' })
  const { of, referer, me, canWrite, intl, setAlternatives, syncPaypal, say } = useCards()
  const [busy, setBusy] = useState(false)
  const [amount, setAmount] = useState('')
  const value = amount ? Number(amount) : null
  const err = alternativeAmountError(value, method, me)

  /** removeMethod: DELETE alternative-payment-methods {id}. */
  const remove = async () => {
    setBusy(true)
    try {
      const r = await of.send<{ success?: boolean }>('DELETE', 'alternative-payment-methods', referer, { id: method.id })
      if (r?.success) {
        void syncPaypal()
        setAlternatives((all) => all.filter((m) => m.id !== method.id))
      }
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ofc-alt">
      <div className="ofc-alt-head">
        <span className="ofc-alt-name">
          {method.title}, {method.country}
        </span>
        <span className="ofc-alt-currency">{method.currency}</span>
        <DotsMenu label={t('card.more')} disabled={busy} items={[{ label: t('alt.delete'), danger: true, disabled: !canWrite, onClick: () => void remove() }]} />
      </div>
      <div className="ofc-alt-params">
        {method.parameters.map((p) => (
          <div key={p.key} className="ofc-alt-param">
            <span>{t(`alt.params.${p.key}`, { defaultValue: p.key })}</span>
            <span>{p.value}</span>
          </div>
        ))}
      </div>
      <form className="ofc-alt-form" onSubmit={(e) => e.preventDefault()}>
        <div className="ofc-alt-field">
          <input
            className={`ofc-input ${err ? 'is-error' : ''}`}
            inputMode="decimal"
            name="amount"
            placeholder={t('alt.enterAmount')}
            aria-label={t('alt.enterAmount')}
            value={amount}
            disabled={!canWrite}
            onChange={(e) => /^\d*(\.\d{0,2})?$/.test(e.target.value) && setAmount(e.target.value)}
          />
          <span className={err ? 'ofc-field-error' : 'ofc-field-hint'}>{err ? t(`alt.${err.key}`, err.values) : convertorMessage(value, method, intl)}</span>
        </div>
        <button type="submit" className="oset-btn oset-btn--primary oset-btn--sm" disabled>
          {t('alt.pay')}
        </button>
      </form>
      <OfOnly text={t('only.topUp')} href={OF_ALTERNATIVE} />
    </div>
  )
}
